import * as React from 'react'
import { ApolloConsumer, Query } from 'react-apollo'
import uuid from 'uuid/v1'

import Loader from 'main/components/common/Loader'
import { AppContext } from 'main/framework'
import { login } from 'main/util/auth'
import { CONTACT_SCHOOL_MODAL_QUERY, REQUEST_INFO_MODAL_QUERY } from './gql'
import { ContactSchoolModal } from './gql/__generated__/ContactSchoolModal'
import { RequestInfoModal } from './gql/__generated__/RequestInfoModal'
import StepContent from './StepContent'
import { track } from './track'
import { IWithExternalSchoolId, IWithSchoolId, SchoolType, Step } from './types'
import { initialStep } from './util'

interface IPropsBase {
  step: Step | null
  onChangeStep: (step: Step | null) => void
  onComplete?: () => void
  onDone: () => void
}

type Props = IPropsBase & (IWithSchoolId | IWithExternalSchoolId)

export default function Content(props: Props) {
  const { step, onChangeStep, onComplete, onDone } = props
  const schoolType = inferSchoolTypeFromProps(props)
  const appContext = React.useContext(AppContext)

  const [sessionId] = React.useState(uuid())
  const trackData = makeTrackData(sessionId, props)
  const [schoolTypeQuery, variableId] = getQueryWithVariables(schoolType, props)

  React.useEffect(() => track('opened', schoolType, trackData), [])

  return (
    <ApolloConsumer>
      {client => (
        <Query
          query={schoolTypeQuery}
          variables={variableId}
          onCompleted={(data: ContactSchoolModal & RequestInfoModal) => {
            if (!step) {
              onChangeStep(initialStep(data, schoolType))
            }
          }}
        >
          {({ data, loading, error }: any) => {
            if (loading || error || !data) {
              return <Loader />
            }

            if (schoolType === SchoolType.Internal && !data.school) {
              throw new Error('no school')
            }

            if (schoolType === SchoolType.External && !data.externalSchool) {
              throw new Error('no external school')
            }

            if (!step) {
              return <Loader />
            }

            return (
              <StepContent
                trackData={trackData}
                step={step}
                onChangeStep={onChangeStep}
                onAuth={credentials => {
                  login(client, credentials, appContext)
                }}
                onComplete={onComplete}
                onDone={handleDone}
              />
            )
          }}
        </Query>
      )}
    </ApolloConsumer>
  )

  function handleDone() {
    track('closed', schoolType, trackData)
    onDone()
  }
}

function inferSchoolTypeFromProps({ schoolId, externalSchoolId }: Props): SchoolType {
  if (schoolId) return SchoolType.Internal
  if (externalSchoolId) return SchoolType.External
  throw new Error('must have schoolId or externalSchoolId in props')
}

function makeTrackData(sessionId: string, { schoolId, externalSchoolId }: Props) {
  if (schoolId) return { schoolId, sessionId }
  if (externalSchoolId) return { externalSchoolId, sessionId }
  throw new Error('must have schoolId or externalSchoolId in props')
}

function getQueryWithVariables(schoolType: SchoolType, props: Props): [any, {}] {
  switch (schoolType) {
    case SchoolType.Internal:
      return [CONTACT_SCHOOL_MODAL_QUERY, { schoolId: props.schoolId }]
    case SchoolType.External:
      return [REQUEST_INFO_MODAL_QUERY, { id: props.externalSchoolId }]
  }
}
